import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import config from "../../config";

export type AuthenticatedFetchReturn<TResponse> = {
    response?: TResponse;
    isLoading: boolean;
    refetch: () => void;
};

const useAuthenticatedFetch = <TResponse,>(
    path: string,
    pauseFetching?: boolean,
): AuthenticatedFetchReturn<TResponse> => {
    const auth = useAuth();
    const [invokedDateTime, setInvokeDateTime] = useState(new Date());
    const [response, setResponse] = useState<TResponse | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const refetch = () => setInvokeDateTime(new Date());

    useEffect(() => {
        (async () => {
            if (pauseFetching) {
                return;
            }

            try {
                setIsLoading(true);
                const url = `${config.carbonApiBaseUrl}/${path}`;
                const token = auth.user?.access_token;

                if (!token) throw new Error("Failed to retrieve access token.");

                const response = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setResponse(await response.json());
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                console.error(error);
            }
        })();
    }, [auth, path, invokedDateTime, pauseFetching]);

    return {
        response,
        isLoading,
        refetch,
    };
};

export default useAuthenticatedFetch;
